import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { AuthShell, inputCls, labelCls, primaryBtn } from "@/components/AuthShell";

export default function Signup() {
  const { signup, continueAsGuest } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    first_name: "", last_name: "", dob: "", email: "", phone: "", password: "", company: "", location: "Lusaka",
  });
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await signup({
        ...form,
        name: `${form.first_name} ${form.last_name}`.trim(),
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        company: form.company.trim() || undefined,
      });
      toast.success("Account created — check your email for a code");
      navigate("/verify-email");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Create your account"
      subtitle="Join Venda Market to start buying and selling."
      footer={<>Already have an account? <Link to="/login" className="font-semibold text-price hover:underline">Sign in</Link></>}
    >
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>First name</label>
            <input data-testid="signup-first-name" required value={form.first_name} onChange={set("first_name")} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Last name</label>
            <input data-testid="signup-last-name" required value={form.last_name} onChange={set("last_name")} className={inputCls} />
          </div>
        </div>
        <div>
          <label className={labelCls}>Company <span className="text-muted-foreground">(optional)</span></label>
          <input data-testid="signup-company" value={form.company} onChange={set("company")} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Date of birth</label>
          <input data-testid="signup-dob" type="date" required value={form.dob} onChange={set("dob")} className={inputCls} />
          <p className="mt-1 text-xs text-muted-foreground">You must be 18 or older.</p>
        </div>
        <div>
          <label className={labelCls}>Email</label>
          <input data-testid="signup-email" type="email" required value={form.email} onChange={set("email")} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Phone</label>
          <input data-testid="signup-phone" required value={form.phone} onChange={set("phone")} className={inputCls} placeholder="+2609…" />
        </div>
        <div>
          <label className={labelCls}>Password</label>
          <div className="relative">
            <input data-testid="signup-password" type={showPw ? "text" : "password"} required value={form.password} onChange={set("password")} className={`${inputCls} pr-11`} placeholder="8+ chars, letters & numbers" />
            <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground" data-testid="signup-toggle-password" aria-label={showPw ? "Hide password" : "Show password"}>
              {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>
        <button data-testid="signup-submit" type="submit" disabled={loading} className={primaryBtn}>
          {loading ? "Creating…" : "Create account"}
        </button>
      </form>
      <button
        data-testid="guest-btn"
        onClick={() => { continueAsGuest(); navigate("/"); }}
        className="mt-3 w-full rounded-xl border border-border py-3 text-sm font-semibold text-foreground transition hover:bg-secondary"
      >
        Continue as guest
      </button>
    </AuthShell>
  );
}
